import { useEffect, useRef } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	Home,
	FolderKanban,
	User,
	Users,
	LogOut,
	X,
	Shield,
	BarChart3,
	FileText,
	Globe,
	Settings,
	Contact,
	Bell,
	Clapperboard,
	Store,
	ExternalLink,
	MessageSquare,
} from "lucide-react";
import { useUnreadCount } from "../../hooks/queries/useNotificationQueries";
import { useSocialDrafts } from "../../hooks/queries/useSocialDraftQueries";
import { SidebarPrompts } from "./sidebar-prompts";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth-context";
import { usePro } from "../../lib/pro-context";
import { getImageUrl } from "../../lib/api/base";
import { MARKETPLACE_URL } from "../../lib/env";
import { LogoIcon } from "../ui/logo-icon";
import { ReleaseBadge } from "../ui/release-badge";

type NavItem = {
	name: string;
	href: string;
	icon: typeof Home;
	roles: string[];
};

const navigation: NavItem[] = [
	{ name: "Dashboard", href: "/", icon: Home, roles: ["owner", "manager", "staff"] },
	{ name: "Analytics", href: "/analytics", icon: BarChart3, roles: ["owner", "manager"] },
	{ name: "Profile", href: "/profile", icon: User, roles: ["owner", "manager"] },
	{ name: "Projects", href: "/projects", icon: FolderKanban, roles: ["owner", "manager", "staff"] },
	{ name: "CRM", href: "/crm", icon: Contact, roles: ["owner", "manager", "staff"] },
	{ name: "Team", href: "/team", icon: Users, roles: ["owner", "manager"] },
	{ name: "Website", href: "/website", icon: Globe, roles: ["owner"] },
	{ name: "Blog", href: "/blogs", icon: FileText, roles: ["owner", "manager"] },
];

type SidebarProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function Sidebar({ isOpen, onClose }: SidebarProps) {
	const { signOut, user, isAdmin, hasProAccess } = useAuth();
	const { proRole, pro, proId, isLoading: isProLoading } = usePro();
	const { data: unreadCount = 0 } = useUnreadCount();
	const { data: socialDraftsData } = useSocialDrafts(proId ?? null);
	const socialBadgeCount =
		(socialDraftsData?.pendingCount ?? 0) +
		(socialDraftsData?.drafts.filter((d) => d.status === "ready").length ?? 0);
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;
	const sidebarRef = useRef<HTMLDivElement>(null);

	// #425: desktop sidebar had no path back to the public marketplace. The
	// link deep-links to the pro's own listing (not the marketplace homepage)
	// because the use case is "let me check how MY profile looks" — landing on
	// the homepage forces the pro to hunt for themselves. Plain <a> because the
	// marketplace is a separate Astro app. Only shown after a super-admin
	// publishes the pro AND the pro has a slug — same gate idiom used by
	// website.tsx (`isProPublished && pro?.slug`). Wait for pro data to settle
	// before deciding to render so the link doesn't flash in after first paint.
	const isProPublished = pro?.status === "published";
	const marketplaceProfileUrl =
		!isProLoading && isProPublished && pro?.slug
			? `${MARKETPLACE_URL}/pros/${pro.slug}`
			: null;

	// Escape key closes sidebar on mobile
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen, onClose]);

	// Auto-focus sidebar when opened on mobile
	useEffect(() => {
		if (isOpen) {
			const firstLink =
				sidebarRef.current?.querySelector<HTMLAnchorElement>("nav a");
			firstLink?.focus();
		}
	}, [isOpen]);

	const handleNavClick = () => {
		// Close sidebar on mobile when navigating
		onClose();
	};

	return (
		<div
			ref={sidebarRef}
			className={cn(
				"fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-background-subtle border-r border-border-default transition-transform duration-300 ease-in-out lg:static lg:translate-x-0",
				isOpen ? "translate-x-0" : "-translate-x-full",
			)}
		>
			{/* Logo */}
			<div className="flex h-16 items-center justify-between px-6 border-b border-border-default">
				<div className="flex items-center gap-1.5">
					<LogoIcon size={36} className="text-foreground-default" />
					<span className="text-xl font-bold text-foreground-default">
						Interioring
					</span>
					<ReleaseBadge />
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close sidebar"
					className="p-2 text-foreground-muted hover:text-foreground-default lg:hidden"
				>
					<X className="h-5 w-5" />
				</button>
			</div>

			{/* Navigation */}
			<nav className="flex flex-col flex-1 space-y-1 px-3 py-4">
				{/* Pro navigation - filtered by role (admins see all items) */}
				{hasProAccess &&
					navigation
						.filter((item) => isAdmin || (proRole && item.roles.includes(proRole)))
						.map((item) => {
						const isActive =
							item.href === "/"
								? currentPath === "/"
								: currentPath.startsWith(item.href);
						return (
							<Link
								key={item.name}
								to={item.href}
								onClick={handleNavClick}
								aria-current={isActive ? "page" : undefined}
								className={cn(
									"group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors",
									isActive
										? "bg-primary-100 text-primary-700"
										: "text-foreground-muted hover:bg-background-muted hover:text-foreground-default",
								)}
							>
								<item.icon
									className={cn(
										"mr-3 h-5 w-5 flex-shrink-0",
										isActive
											? "text-primary-600"
											: "text-foreground-muted group-hover:text-foreground-default",
									)}
								/>
								{item.name}
							</Link>
						);
					})}

				{/* Social Studio */}
				{hasProAccess && (
					<Link
						to="/social-studio"
						onClick={handleNavClick}
						aria-current={currentPath.startsWith("/social-studio") ? "page" : undefined}
						className={cn(
							"group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors",
							currentPath.startsWith("/social-studio")
								? "bg-primary-100 text-primary-700"
								: "text-foreground-muted hover:bg-background-muted hover:text-foreground-default",
						)}
					>
						<Clapperboard
							className={cn(
								"mr-3 h-5 w-5 flex-shrink-0",
								currentPath.startsWith("/social-studio")
									? "text-primary-600"
									: "text-foreground-muted group-hover:text-foreground-default",
							)}
						/>
						Social Studio
						{socialBadgeCount > 0 && (
							<span className="ml-auto bg-primary-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
								{socialBadgeCount > 99 ? "99+" : socialBadgeCount}
							</span>
						)}
					</Link>
				)}

				{/* Admin link - only visible to admins */}
				{isAdmin && (
					<>
						{hasProAccess && (
							<div className="my-4 border-t border-border-default" />
						)}
						<Link
							to="/admin"
							onClick={handleNavClick}
							aria-current={currentPath.startsWith("/admin") ? "page" : undefined}
							className={cn(
								"group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors",
								currentPath.startsWith("/admin")
									? "bg-secondary-100 text-secondary-700"
									: "text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700",
							)}
						>
							<Shield
								className={cn(
									"mr-3 h-5 w-5 flex-shrink-0",
									currentPath.startsWith("/admin")
										? "text-secondary-600"
										: "text-secondary-400 group-hover:text-secondary-600",
								)}
							/>
							Admin Panel
						</Link>
					</>
				)}

				{/* Spacer */}
				<div className="flex-1" />

				{/* Marketplace — deep-link to the pro's public listing (#425).
				   External anchor, not a TanStack <Link>, because the
				   marketplace is a separate Astro app. New tab so pros can peek
				   at their public profile and return to in-flight work in the
				   portal. Hidden when the pro is unpublished or has no slug —
				   either case means there's no public profile URL to link to. */}
				{hasProAccess && marketplaceProfileUrl && (
					<a
						href={marketplaceProfileUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-center rounded-md px-3 py-2 text-sm font-medium text-foreground-muted hover:bg-background-muted hover:text-foreground-default transition-colors"
					>
						<Store className="mr-3 h-5 w-5 flex-shrink-0 text-foreground-muted group-hover:text-foreground-default" />
						Marketplace
						<ExternalLink className="ml-auto h-4 w-4 text-foreground-muted/60" />
					</a>
				)}

				{/* Push + Install prompts */}
				<SidebarPrompts />

				{/* Feedback (#591): desktop sidebar entry that opens the existing
				   FeedbackButton form via the same "open-feedback" event the
				   mobile MoreSheet uses. The floating button alone was reported
				   as undiscoverable — this gives pros a labelled surface in the
				   nav without duplicating the form component. */}
				{hasProAccess && (
					<button
						type="button"
						onClick={() => {
							handleNavClick();
							window.dispatchEvent(new CustomEvent("open-feedback"));
						}}
						className="group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium text-foreground-muted hover:bg-background-muted hover:text-foreground-default transition-colors"
					>
						<MessageSquare className="mr-3 h-5 w-5 flex-shrink-0 text-foreground-muted group-hover:text-foreground-default" />
						Feedback
					</button>
				)}

				{/* Notifications */}
				<Link
					to="/notifications"
					onClick={handleNavClick}
					aria-current={currentPath.startsWith("/notifications") ? "page" : undefined}
					className={cn(
						"group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors",
						currentPath.startsWith("/notifications")
							? "bg-primary-100 text-primary-700"
							: "text-foreground-muted hover:bg-background-muted hover:text-foreground-default",
					)}
				>
					<Bell
						className={cn(
							"mr-3 h-5 w-5 flex-shrink-0",
							currentPath.startsWith("/notifications")
								? "text-primary-600"
								: "text-foreground-muted group-hover:text-foreground-default",
						)}
					/>
					Notifications
					{unreadCount > 0 && (
						<span className="ml-auto bg-error text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
							{unreadCount > 99 ? "99+" : unreadCount}
						</span>
					)}
				</Link>
			</nav>

			{/* User section */}
			<div className="border-t border-border-default p-4">
				<div className="flex items-center">
					<Link
						to="/settings"
						onClick={handleNavClick}
						className="flex items-center flex-1 min-w-0 group rounded-md -m-1 p-1 hover:bg-background-muted transition-colors"
					>
						<div className="flex-shrink-0">
							{user?.image ? (
								<img
									src={getImageUrl(user.image)}
									alt=""
									className="h-8 w-8 rounded-full object-cover"
								/>
							) : (
								<div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
									<span className="text-sm font-medium text-primary-700">
										{user?.name?.charAt(0).toUpperCase() || "U"}
									</span>
								</div>
							)}
						</div>
						<div className="ml-3 flex-1 min-w-0">
							<p className="text-sm font-medium text-foreground-default truncate">
								{user?.name}
							</p>
							<p className="text-xs text-foreground-muted truncate">
								{user?.email}
							</p>
						</div>
						<Settings className="h-4 w-4 text-foreground-muted group-hover:text-foreground-default flex-shrink-0" />
					</Link>
					<button
						type="button"
						onClick={signOut}
						className="ml-1 p-1 text-foreground-muted hover:text-foreground-default transition-colors"
						title="Sign out"
					>
						<LogOut className="h-5 w-5" />
					</button>
				</div>
			</div>
		</div>
	);
}
